import React from 'react'
import PropTypes from 'prop-types'
import { Box, Typography } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { getDisclosures } from './../utils/disclosures-util'
import PurchaseDetailsBlock from './PurchaseDetailsBlock'

const disclosures = getDisclosures()

const PurchaseDetails = ({ dealData }) => {
    const { t } = useTranslation()

    const {
        data: {
            vehicleFinancing: {
                monthlyPayment,
                annualPercentageRate,
                term,
                annualMileage,
                monthlyPaymentsDue,
                secondMonthlyPaymentsDue
            } = {},
            dueAtSigning: { totalAmountsDueAtLeaseSigningOrDelivery } = {},
            amountFinanced,
            transactionType,
            cashDueAtDealer
        } = {
            vehicle: {},
            customer: {},
            dealerInfo: {},
            vehicleFinancing: {},
            taxesAndFees: {},
            dueAtSigning: {}
        }
    } = dealData || {}

    return (
        <Box
            sx={{
                mx: { xs: 3, md: 0 },
                borderRadius: '16px',
                display: 'flex',
                flexDirection: 'column',
                justifyItems: 'center',
                gap: 4,
                px: 5,
                py: 6,
                backgroundImage: 'linear-gradient(128deg, #F5F6F7 35.5%, #FEFEFE 140.67%)'
            }}
        >
            <Typography
                component="div"
                data-testid="transaction-type"
                className="transaction_type_name"
                sx={{
                    color: 'common.charcoal.main',
                    lineHeight: '28px',
                    textAlign: 'center',
                    typography: 'regularSubTitle'
                }}
            >
                {transactionType === 'Cash' && t('cash_purchase_details')}
                {transactionType === 'Retail' && t('ford_credit_financing_details')}
                {transactionType === 'FlexBuy' && t('flexbuy_details')}
                {transactionType === 'Lease' && t('leasing_details')}
                {transactionType === 'Financing' && t('finance_details')}
            </Typography>

            <Box
                sx={{
                    display: 'flex',
                    gap: { xs: 6, lg: 16 },
                    justifyContent: 'space-evenly',
                    flexWrap: { xs: 'wrap', smd: 'initial' }
                }}
            >
                {transactionType === 'Cash' && (
                    <PurchaseDetailsBlock
                        testId={'amount-due-at-dealer'}
                        name={disclosures.cashDueAtDealer.name}
                        order={disclosures.cashDueAtDealer.order}
                        value={cashDueAtDealer}
                        amount={true}
                    />
                )}

                {transactionType === 'Financing' && (
                    <PurchaseDetailsBlock
                        testId={'amount-financed'}
                        name={disclosures.amountFinanced.name}
                        order={disclosures.amountFinanced.order}
                        value={amountFinanced}
                        amount={true}
                        dealerLabel={true}
                    />
                )}

                {transactionType === 'Retail' && (
                    <>
                        <PurchaseDetailsBlock
                            testId={'amount-financed'}
                            name={disclosures.amountFinanced.name}
                            order={disclosures.amountFinanced.order}
                            value={amountFinanced}
                            amount={true}
                        />

                        <PurchaseDetailsBlock
                            testId={'monthly-payment'}
                            name={disclosures.monthlyPayment.name}
                            order={disclosures.monthlyPayment.order}
                            value={monthlyPayment}
                            amount={true}
                            moLabel={true}
                        />

                        <PurchaseDetailsBlock
                            testId={'apr'}
                            name={disclosures.annualPercentageRate.name}
                            order={disclosures.annualPercentageRate.order}
                            value={annualPercentageRate}
                            percentage={true}
                        />

                        <PurchaseDetailsBlock
                            testId={'term'}
                            name={t('term')}
                            order={disclosures.term.order}
                            value={term}
                            label={true}
                            mosLable={true}
                        />
                    </>
                )}

                {transactionType === 'Lease' && (
                    <>
                        <PurchaseDetailsBlock
                            testId={'monthly-payment'}
                            name={disclosures.monthlyPayment.name}
                            order={disclosures.monthlyPayment.order}
                            value={monthlyPayment}
                            amount={true}
                            moLabel={true}
                        />

                        <PurchaseDetailsBlock
                            testId={'term'}
                            name={t('term')}
                            order={disclosures.term.order}
                            value={term}
                            label={true}
                            mosLable={true}
                        />

                        <PurchaseDetailsBlock
                            testId={'mileage'}
                            name={t('mileage')}
                            order={disclosures.annualMileage.order}
                            value={annualMileage}
                            yrLable={true}
                        />

                        <PurchaseDetailsBlock
                            testId={'amount-due-signing'}
                            name={t('amount_due_at_signing')}
                            order={disclosures.totalAmountsDueAtLeaseSigningOrDelivery.order}
                            value={totalAmountsDueAtLeaseSigningOrDelivery}
                            amount={true}
                        />
                    </>
                )}

                {transactionType === 'FlexBuy' && (
                    <>
                        <PurchaseDetailsBlock
                            testId={'amount-financed'}
                            name={disclosures.amountFinanced.name}
                            order={disclosures.amountFinanced.order}
                            value={amountFinanced}
                            amount={true}
                        />

                        <PurchaseDetailsBlock
                            testId={'monthly-payment-1'}
                            name={`${t('monthly_payment')} (1-36)`}
                            order={disclosures.monthlyPaymentsDue.order}
                            value={monthlyPaymentsDue}
                            amount={true}
                            moLabel={true}
                        />

                        <PurchaseDetailsBlock
                            testId={'monthly-payment-2'}
                            name={`${t('monthly_payment')} (37-${term})`}
                            order={disclosures.secondMonthlyPaymentsDue.order}
                            value={secondMonthlyPaymentsDue}
                            amount={true}
                            moLabel={true}
                        />

                        <PurchaseDetailsBlock
                            testId={'apr'}
                            name={disclosures.annualPercentageRate.name}
                            order={disclosures.annualPercentageRate.order}
                            value={annualPercentageRate}
                            percentage={true}
                        />
                    </>
                )}
            </Box>
        </Box>
    )
}

PurchaseDetails.propTypes = {
    dealData: PropTypes.object
}
export default PurchaseDetails
